import { ICONS, type IconId } from './index';

/** An inline icon in text: `:beer:`. Ids are lowercase words joined by dashes. */
export const TOKEN = /:([a-z][a-z0-9]*(?:-[a-z0-9]+)*):/g;

export const isIconId = (s: string): s is IconId => Object.prototype.hasOwnProperty.call(ICONS, s);

/** Text split into plain runs and icons (unknown `:words:` stay as text). */
export function parseRich(text: string): Array<string | { icon: IconId }> {
  const out: Array<string | { icon: IconId }> = [];
  if (!text.includes(':')) return text ? [text] : out;
  let last = 0;
  for (const m of text.matchAll(TOKEN)) {
    const id = m[1] as string;
    if (!isIconId(id)) continue;
    const at = m.index ?? 0;
    if (at > last) out.push(text.slice(last, at));
    out.push({ icon: id });
    last = at + m[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

/** What an icon is called, for screen readers and plain-text places. */
export const iconLabel = (id: IconId): string => id.replace(/-/g, ' ');

/**
 * Text with its icons spelled out, for places that can only take plain text: tooltips,
 * aria labels, the clipboard. `withIcons: false` drops them instead.
 */
export function plainText(text: string, withIcons = false): string {
  const parts = parseRich(text);
  return parts
    .map((p) => (typeof p === 'string' ? p : withIcons ? iconLabel(p.icon) : ''))
    .join('')
    .replace(/ {2,}/g, ' ')
    .trim();
}

/** Length in typed units: each character counts one, and so does each icon. */
export const richLength = (text: string): number =>
  parseRich(text).reduce((n, p) => n + (typeof p === 'string' ? p.length : 1), 0);

/** The first `units` of a text (see richLength), never cutting a `:token:` in half. */
export function richSlice(text: string, units: number): string {
  let left = units;
  let out = '';
  for (const p of parseRich(text)) {
    if (left <= 0) break;
    if (typeof p === 'string') {
      out += p.slice(0, left);
      left -= p.length;
    } else {
      out += `:${p.icon}:`;
      left -= 1;
    }
  }
  return out;
}

/** The icon a lone token stands for (`':beer:'` gives `'beer'`), or null. */
export function iconOf(token: string): IconId | null {
  const m = /^:([a-z][a-z0-9-]*):$/.exec(token);
  return m && isIconId(m[1] as string) ? (m[1] as IconId) : null;
}
